import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function RepurchaseCreditModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation('app');
  const [rate, setRate] = useState(1.0);
  const [amount, setAmount] = useState('');
  const [estimatedCredit, setEstimatedCredit] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchRate();
      setAmount('');
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (amount && !isNaN(amount)) {
      setEstimatedCredit(Math.round(parseFloat(amount) * rate * 100) / 100);
    } else {
      setEstimatedCredit(0);
    }
  }, [amount, rate]);

  const fetchRate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/credit-repurchase-rate`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setRate(data.rate || 1.0);
      }
    } catch (err) {
      console.error('Failed to fetch rate:', err);
    }
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError(t('repurchaseModal.invalidAmount'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/repurchase-credits`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ cash_amount: parseFloat(amount) }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'INSUFFICIENT_CASH') {
          setError(t('repurchaseModal.insufficientCash'));
        } else if (data.error === 'INVALID_AMOUNT') {
          setError(t('repurchaseModal.invalidAmount'));
        } else {
          setError(data.message || t('repurchaseModal.error'));
        }
        return;
      }

      setSuccess(true);
      setAmount('');
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Repurchase error:', err);
      setError(err.message || t('repurchaseModal.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('repurchaseModal.title')}
          </h2>
        </div>

        <div className="p-6 space-y-4">
          {success ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded text-green-700 text-center">
              {t('repurchaseModal.success')}
            </div>
          ) : (
            <>
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">{t('repurchaseModal.currentRate')}</p>
                  <p className="text-lg font-bold text-gray-900">
                    1 Cash Point (CP) = {rate.toFixed(4)} Credit Points (SP)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('repurchaseModal.cashAmount')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="100.00"
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {amount && (
                  <div className="p-3 bg-blue-50 rounded">
                    <p className="text-sm text-gray-600">{t('repurchaseModal.youWillReceive')}</p>
                    <p className="text-lg font-bold text-blue-600">
                      {estimatedCredit.toFixed(2)} Credit Points (SP)
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
                >
                  {t('repurchaseModal.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !amount || parseFloat(amount) <= 0}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition disabled:opacity-50"
                >
                  {loading ? t('repurchaseModal.processing') : t('repurchaseModal.convert')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
